import { ThumbnailInfo } from '@app/core/hooks/api/useApiThumbnailsRange';
import { renderMaybeFn } from '@mi-front/core';
import { useEffect, useState } from 'react';
import { Stream, StreamProps } from '../stream/Stream';
import { ThumbnailsLoading } from './ThumbnailsLoading';
import { ThumbnailsRollV2 } from './ThumbnailsRollV2';
import { useThumbnailsStreamSegments } from './useThumbnailsStreamSegments';
import { useTimestampedThumbnails } from './useTimestampedThumbnails';

interface Props extends StreamProps {
  readonly thumbnails: ThumbnailInfo[];
  readonly thumbnailsLoading: boolean;
}

const THUMB_HEIGHT = 81;

/**
 * Stream preview that renders a time-aligned thumbnail filmstrip via ThumbnailsRollV2.
 * Fetches segments and joins them with thumbnails internally.
 */
export function ThumbnailsStreamV2(p: Props) {
  const { thumbnails, thumbnailsLoading, range, ...rest } = p;
  const { segments, isLoading: segmentsLoading } = useThumbnailsStreamSegments(range);
  const timestampedThumbnails = useTimestampedThumbnails(thumbnails, segments);
  const isRollLoading = thumbnailsLoading || segmentsLoading;

  const [showLoading, setShowLoading] = useState(true);

  // Hide loading after 3 seconds when thumbnails loaded
  // This is to avoid the it closing too early before the css sprites
  // in the roll are completely displayed
  useEffect(() => {
    if (!isRollLoading) {
      setTimeout(() => {
        setShowLoading(false);
      }, 3000);
    }
  }, [isRollLoading]);

  return (
    <Stream
      {...rest}
      range={range}
      compressed
      htmlBehindSvg
      renderHtml={() => showLoading && <ThumbnailsLoading />}
      renderSvg={() => (
        <>
          {!isRollLoading && (
            <ThumbnailsRollV2 thumbHeight={THUMB_HEIGHT} thumbnails={timestampedThumbnails} />
          )}
          {renderMaybeFn(p.renderSvg)}
        </>
      )}
    />
  );
}
